import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { IngestionController } from '../controllers/ingestion-controller.js';
import type { ExtractedRequirement, IngestionApiKeys } from '../api/ingestion-types.js';
import { REQUIREMENT_PRIORITIES } from '../utils/constants.js';
import './sysml-ingestion-journal.js';

// ---------------------------------------------------------------------------
// <sysml-ingest-panel> — Requirements Document Ingestion UI
//
// Phase-driven rendering that follows the IngestionController state machine:
// idle → uploading → extracting → reviewing → checking-duplicates → committing → done
//
// Follows sysml-requirements-panel.ts pattern: baseStyles + panelStyles + badgeStyles,
// panel-header + panel-body layout, card-based items.
// ---------------------------------------------------------------------------

@customElement('sysml-ingest-panel')
export class SysmlIngestPanel extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host { display: flex; flex-direction: column; height: 100%; }

      /* ── Upload zone ── */
      .upload-zone {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--r5-space-3);
        padding: var(--r5-space-6);
        margin: var(--r5-space-3);
        border: 2px dashed var(--r5-border);
        border-radius: var(--r5-radius-lg);
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
        text-align: center;
        color: var(--r5-text-muted);
      }
      .upload-zone:hover,
      .upload-zone.drag-over {
        border-color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 5%, transparent);
        color: var(--r5-text);
      }
      .upload-zone-icon {
        width: 40px;
        height: 40px;
        color: var(--r5-text-muted);
      }
      .upload-zone:hover .upload-zone-icon { color: var(--r5-primary); }
      .upload-hint {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }
      .upload-actions {
        display: flex;
        gap: var(--r5-space-2);
        margin-top: var(--r5-space-2);
      }
      input[type="file"] { display: none; }

      /* ── Paste area ── */
      .paste-area {
        margin: 0 var(--r5-space-3) var(--r5-space-3);
      }
      .paste-area textarea {
        width: 100%;
        min-height: 100px;
        padding: var(--r5-space-2);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-text-xs);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        color: var(--r5-text);
        resize: vertical;
        box-sizing: border-box;
      }
      .paste-area textarea:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }

      /* ── Progress ── */
      .progress {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: var(--r5-space-3);
        padding: var(--r5-space-6);
        color: var(--r5-text-muted);
      }
      .progress-bar {
        width: 100%;
        height: 4px;
        border-radius: 2px;
        background: var(--r5-surface-muted);
        overflow: hidden;
      }
      .progress-bar-inner {
        height: 100%;
        background: var(--r5-primary);
        border-radius: 2px;
        animation: progress-pulse 1.5s ease-in-out infinite;
      }
      @keyframes progress-pulse {
        0%, 100% { width: 30%; margin-left: 0; }
        50% { width: 60%; margin-left: 20%; }
      }

      /* ── Requirement cards ── */
      .req-card {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        padding: var(--r5-space-2) var(--r5-space-3);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
      }
      .req-card:hover {
        border-color: color-mix(in srgb, var(--r5-primary) 40%, var(--r5-border));
        background: color-mix(in srgb, var(--r5-primary) 2%, transparent);
      }
      .req-card.rejected {
        opacity: 0.5;
        border-style: dashed;
      }
      .req-card.has-duplicates {
        border-color: var(--r5-warning-400);
      }
      .req-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--r5-space-2);
      }
      .req-name {
        font-weight: 600;
        font-size: var(--r5-text-xs);
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .req-badges {
        display: flex;
        gap: var(--r5-space-1);
        flex-shrink: 0;
      }
      .req-text {
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
        line-height: 1.4;
        max-height: 3em;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
      .req-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-top: var(--r5-space-1);
      }
      .req-actions {
        display: flex;
        gap: var(--r5-space-1);
        opacity: 0;
        transition: opacity var(--r5-duration-fast) var(--r5-ease-default);
      }
      .req-card:hover .req-actions { opacity: 1; }

      /* ── Confidence badge ── */
      .confidence-high { background: var(--r5-success-50); color: var(--r5-success-700); border: 1px solid var(--r5-success-200); }
      .confidence-medium { background: var(--r5-warning-50); color: var(--r5-warning-700); border: 1px solid var(--r5-warning-200); }
      .confidence-low { background: var(--r5-destructive-50); color: var(--r5-destructive-700); border: 1px solid var(--r5-destructive-200); }

      /* ── Duplicate warning ── */
      .dup-warning {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        padding: var(--r5-space-2);
        margin-top: var(--r5-space-1);
        background: var(--r5-warning-50);
        border: 1px solid var(--r5-warning-200);
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
      }
      .dup-match {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: var(--r5-space-1) 0;
      }
      .dup-match-name {
        font-weight: 500;
        color: var(--r5-warning-700);
      }
      .dup-similarity {
        font-family: var(--r5-font-mono);
        font-size: var(--r5-font-size-badge);
        color: var(--r5-warning-700);
      }

      /* ── Code preview ── */
      .code-preview {
        margin: var(--r5-space-2) var(--r5-space-3);
        padding: var(--r5-space-3);
        background: var(--r5-surface-muted);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        font-family: var(--r5-font-mono);
        font-size: var(--r5-font-size-badge);
        line-height: 1.5;
        white-space: pre-wrap;
        max-height: 300px;
        overflow: auto;
      }

      /* ── Diagnostics list ── */
      .diagnostics {
        margin: var(--r5-space-2) var(--r5-space-3);
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
      }
      .diag-item {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) var(--r5-space-2);
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
      }
      .diag-error { background: var(--r5-destructive-50); color: var(--r5-destructive-700); }
      .diag-warning { background: var(--r5-warning-50); color: var(--r5-warning-700); }

      /* ── Settings overlay ── */
      .settings {
        padding: var(--r5-space-3);
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-3);
      }
      .settings-field {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
      }
      .settings-field label {
        font-size: var(--r5-text-xs);
        font-weight: 500;
        color: var(--r5-text-muted);
      }
      .settings-field input, .settings-field select {
        width: 100%;
        padding: var(--r5-space-2);
        font-family: var(--r5-font-sans);
        font-size: var(--r5-text-xs);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        color: var(--r5-text);
        box-sizing: border-box;
      }
      .settings-field input:focus, .settings-field select:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }
      .settings-section {
        font-size: var(--r5-text-xs);
        font-weight: 600;
        margin-top: var(--r5-space-2);
        padding-bottom: var(--r5-space-1);
        border-bottom: 1px solid var(--r5-border);
      }
      .settings-actions {
        display: flex;
        gap: var(--r5-space-2);
        justify-content: flex-end;
        margin-top: var(--r5-space-2);
      }

      /* ── Summary stats row ── */
      .stats-row {
        display: flex;
        gap: var(--r5-space-3);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
      .stat {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
      }
      .stat-value {
        font-weight: 600;
        color: var(--r5-text);
      }

      /* ── Review toolbar ── */
      .review-toolbar {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        flex-shrink: 0;
      }

      /* ── List ── */
      .list {
        padding: var(--r5-space-2);
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-2);
      }

      /* ── Inline edit ── */
      .edit-overlay {
        padding: var(--r5-space-3);
        border: 2px solid var(--r5-primary);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        box-shadow: var(--r5-shadow-sm);
      }
      .edit-field {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        margin-bottom: var(--r5-space-2);
      }
      .edit-field label {
        font-size: var(--r5-text-xs);
        font-weight: 500;
        color: var(--r5-text-muted);
      }
      .edit-field input, .edit-field textarea, .edit-field select {
        width: 100%;
        padding: var(--r5-space-2);
        font-family: var(--r5-font-sans);
        font-size: var(--r5-text-xs);
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-md);
        background: var(--r5-surface);
        color: var(--r5-text);
        box-sizing: border-box;
      }
      .edit-field textarea { min-height: 60px; resize: vertical; }
      .edit-field input:focus, .edit-field textarea:focus, .edit-field select:focus {
        outline: 2px solid var(--r5-primary);
        outline-offset: -1px;
      }
      .edit-actions {
        display: flex;
        gap: var(--r5-space-2);
        justify-content: flex-end;
      }

      /* ── Done state ── */
      .done-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--r5-space-3);
        padding: var(--r5-space-6);
        text-align: center;
      }
      .done-icon {
        width: 48px;
        height: 48px;
        color: var(--r5-success-700);
      }

      /* ── Warnings ── */
      .warnings {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
        margin: var(--r5-space-2) var(--r5-space-3);
      }
      .warning-item {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-1) var(--r5-space-2);
        background: var(--r5-warning-50);
        border: 1px solid var(--r5-warning-200);
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
        color: var(--r5-warning-700);
      }

      /* ── Error bar ── */
      .error-bar {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        background: var(--r5-destructive-50);
        border-bottom: 1px solid var(--r5-destructive-200);
        font-size: var(--r5-text-xs);
        color: var(--r5-destructive-700);
        flex-shrink: 0;
      }
      .error-bar ark-button { margin-left: auto; }
    `,
  ];

  @property({ attribute: false })
  ingestion!: IngestionController;

  /** Primitive generation counter — drives reactivity from parent chain */
  @property({ attribute: false, type: Number }) ingestionGeneration = 0;

  @state() private _showJournal = false;
  @state() private _dragOver = false;
  @state() private _showPaste = false;
  @state() private _pasteText = '';
  @state() private _editingId: string | null = null;
  @state() private _editName = '';
  @state() private _editTitle = '';
  @state() private _editText = '';
  @state() private _editPriority = 'medium';

  // Settings form state
  @state() private _settingsAnthropicKey = '';
  @state() private _settingsEmbeddingKey = '';
  @state() private _settingsEmbeddingProvider: 'voyage' | 'openai' = 'voyage';
  @state() private _settingsVectorProvider: 'pinecone' | 'chroma' = 'pinecone';
  @state() private _settingsVectorEndpoint = '';
  @state() private _settingsVectorIndex = '';
  @state() private _settingsVectorApiKey = '';
  @state() private _connectionTestResult: string | null = null;

  override render() {
    // Settings overlay takes priority
    if (this.ingestion.showSettings) {
      return html`
        ${this._renderHeader()}
        <div class="panel-body" style="padding: 0;">
          ${this._renderSettings()}
        </div>
      `;
    }

    // Journal view
    if (this._showJournal) {
      return html`
        ${this._renderHeader()}
        <sysml-ingestion-journal
          .activeJournal=${this.ingestion.activeJournal}
          .journals=${this.ingestion.journals}
          .generation=${this.ingestionGeneration}
          @close-journal=${() => { this._showJournal = false; }}
        ></sysml-ingestion-journal>
      `;
    }

    return html`
      ${this._renderHeader()}
      ${this.ingestion.error ? this._renderErrorBar() : nothing}
      ${this._renderPhaseContent()}
    `;
  }

  // ── Header ──────────────────────────────────────────────────────────────

  private _renderHeader() {
    const phase = this.ingestion.phase;
    const title =
      phase === 'idle' ? 'Ingest Requirements'
      : phase === 'uploading' || phase === 'extracting' ? 'Extracting...'
      : phase === 'reviewing' || phase === 'checking-duplicates' ? `Review (${this.ingestion.acceptedRequirements.length}/${this.ingestion.requirements.length})`
      : phase === 'committing' ? 'Commit SysML'
      : phase === 'done' ? 'Complete'
      : 'Ingest';

    const hasJournalData = !!this.ingestion.activeJournal || this.ingestion.journals.length > 0;

    return html`
      <div class="panel-header">
        <h3>${title}</h3>
        <div style="display: flex; gap: var(--r5-space-1);">
          ${phase !== 'idle' && phase !== 'done'
            ? html`
                <ark-button size="xs" variant="ghost" @click=${() => this.ingestion.reset()} title="Start over">
                  <ark-icon name="rotate-ccw" size="xs"></ark-icon>
                </ark-button>
              `
            : nothing}
          ${hasJournalData
            ? html`
                <ark-button
                  size="xs"
                  variant="ghost"
                  @click=${this._openAuditTrail}
                  title="Full audit report"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                    <polyline points="10 9 9 9 8 9"/>
                  </svg>
                </ark-button>
                <ark-button
                  size="xs"
                  variant=${this._showJournal ? 'outline' : 'ghost'}
                  @click=${() => { this._showJournal = !this._showJournal; }}
                  title="Ingestion journal"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/>
                    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                  </svg>
                </ark-button>
              `
            : nothing}
          <ark-button
            size="xs"
            variant="ghost"
            @click=${() => {
              this.ingestion.showSettings = !this.ingestion.showSettings;
              this._loadSettingsValues();
              this.requestUpdate();
            }}
            title="Settings"
          >
            <ark-icon name="settings" size="xs"></ark-icon>
          </ark-button>
        </div>
      </div>
    `;
  }

  // ── Error bar ───────────────────────────────────────────────────────────

  private _renderErrorBar() {
    return html`
      <div class="error-bar">
        <ark-icon name="alert-circle" size="xs"></ark-icon>
        ${this.ingestion.error}
        <ark-button size="xs" variant="ghost" @click=${() => { this.ingestion.error = null; this.requestUpdate(); }}>
          <ark-icon name="x" size="xs"></ark-icon>
        </ark-button>
      </div>
    `;
  }

  // ── Phase router ────────────────────────────────────────────────────────

  private _renderPhaseContent() {
    switch (this.ingestion.phase) {
      case 'idle':
        return this._renderIdlePhase();
      case 'uploading':
      case 'extracting':
        return this._renderExtractingPhase();
      case 'reviewing':
      case 'checking-duplicates':
        return this._renderReviewPhase();
      case 'committing':
        return this._renderCommitPhase();
      case 'done':
        return this._renderDonePhase();
      default:
        return this._renderIdlePhase();
    }
  }

  // ── IDLE phase: upload zone + paste ─────────────────────────────────────

  private _renderIdlePhase() {
    return html`
      <div class="panel-body" style="padding: 0;">
        <div
          class="upload-zone ${this._dragOver ? 'drag-over' : ''}"
          @click=${this._onUploadClick}
          @dragover=${this._onDragOver}
          @dragleave=${this._onDragLeave}
          @drop=${this._onDrop}
        >
          <svg class="upload-zone-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
            <polyline points="17 8 12 3 7 8"/>
            <line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          <div>
            <strong>Drop a file here</strong> or click to browse
          </div>
          <div class="upload-hint">
            Images (JPEG, PNG, WebP), Text, CSV
          </div>
          <input
            type="file"
            id="file-input"
            accept="image/jpeg,image/png,image/gif,image/webp,text/plain,text/csv,.txt,.csv"
            @change=${this._onFileSelected}
          />
        </div>

        <div class="upload-actions" style="justify-content: center; padding-bottom: var(--r5-space-3);">
          <ark-button size="xs" variant="outline" @click=${() => (this._showPaste = !this._showPaste)}>
            <ark-icon name="clipboard-list" size="xs"></ark-icon>
            ${this._showPaste ? 'Hide' : 'Paste text'}
          </ark-button>
        </div>

        ${this._showPaste
          ? html`
              <div class="paste-area">
                <textarea
                  placeholder="Paste requirements text here..."
                  .value=${this._pasteText}
                  @input=${(e: InputEvent) => (this._pasteText = (e.target as HTMLTextAreaElement).value)}
                ></textarea>
                <div style="display: flex; justify-content: flex-end; margin-top: var(--r5-space-2);">
                  <ark-button
                    size="xs"
                    variant="primary"
                    ?disabled=${!this._pasteText.trim()}
                    @click=${this._onPasteSubmit}
                  >
                    Extract Requirements
                  </ark-button>
                </div>
              </div>
            `
          : nothing}

        ${!this.ingestion.isConfigured
          ? html`
              <div class="empty-state" style="padding-top: 0;">
                <ark-icon name="key" size="sm"></ark-icon>
                <div>Configure your API key to get started</div>
                <ark-button size="xs" variant="outline" @click=${() => {
                  this.ingestion.showSettings = true;
                  this._loadSettingsValues();
                  this.requestUpdate();
                }}>
                  Open Settings
                </ark-button>
              </div>
            `
          : nothing}
      </div>
    `;
  }

  // ── EXTRACTING phase: progress indicator ────────────────────────────────

  private _renderExtractingPhase() {
    return html`
      <div class="panel-body" style="padding: 0;">
        <div class="progress">
          <ark-icon name="sparkles" size="lg"></ark-icon>
          <div>${this.ingestion.progressMessage || 'Processing...'}</div>
          <div class="progress-bar">
            <div class="progress-bar-inner"></div>
          </div>
          ${this.ingestion.uploadSource
            ? html`<div class="upload-hint">${this.ingestion.uploadSource.filename}</div>`
            : nothing}
          <ark-button size="xs" variant="ghost" @click=${() => this.ingestion.reset()}>
            Cancel
          </ark-button>
        </div>
      </div>
    `;
  }

  // ── REVIEWING phase: editable requirement cards ─────────────────────────

  private _renderReviewPhase() {
    const reqs = this.ingestion.requirements;
    const accepted = this.ingestion.acceptedRequirements;
    const isChecking = this.ingestion.phase === 'checking-duplicates';

    return html`
      <div class="stats-row">
        <div class="stat">
          <span class="stat-value">${reqs.length}</span> extracted
        </div>
        <div class="stat">
          <span class="stat-value">${accepted.length}</span> accepted
        </div>
        <div class="stat">
          Confidence: <span class="stat-value">${this.ingestion.totalConfidence}%</span>
        </div>
      </div>

      <div class="review-toolbar">
        <ark-button size="xs" variant="ghost" @click=${() => this.ingestion.acceptAll()}>
          Accept All
        </ark-button>
        <ark-button size="xs" variant="ghost" @click=${() => this.ingestion.rejectAll()}>
          Reject All
        </ark-button>
        <span style="flex: 1;"></span>
        ${this.ingestion.isVectorConfigured
          ? html`
              <ark-button
                size="xs"
                variant="outline"
                ?disabled=${isChecking}
                @click=${() => this.ingestion.checkDuplicates()}
              >
                <ark-icon name="copy" size="xs"></ark-icon>
                ${isChecking ? 'Checking...' : 'Check Duplicates'}
              </ark-button>
            `
          : nothing}
        <ark-button
          size="xs"
          variant="primary"
          ?disabled=${accepted.length === 0 || this.ingestion.isLoading}
          @click=${() => this.ingestion.generateSysml()}
        >
          Generate SysML
        </ark-button>
      </div>

      ${this.ingestion.warnings.length > 0
        ? html`
            <div class="warnings">
              ${this.ingestion.warnings.map(
                (w) => html`
                  <div class="warning-item">
                    <ark-icon name="alert-triangle" size="xs"></ark-icon>
                    ${w}
                  </div>
                `,
              )}
            </div>
          `
        : nothing}

      <div class="panel-body" style="padding: 0;">
        <div class="list">
          ${reqs.map((req) =>
            this._editingId === req.id
              ? this._renderEditCard(req)
              : this._renderReqCard(req),
          )}
        </div>
      </div>
    `;
  }

  private _renderReqCard(req: ExtractedRequirement) {
    const confClass =
      req.confidence >= 0.9 ? 'confidence-high'
      : req.confidence >= 0.7 ? 'confidence-medium'
      : 'confidence-low';

    const priorityDef = REQUIREMENT_PRIORITIES.find(
      (p) => p.value === req.priority,
    );

    const hasDups = req.duplicates && req.duplicates.length > 0;

    return html`
      <div class="req-card ${!req.accepted ? 'rejected' : ''} ${hasDups ? 'has-duplicates' : ''}">
        <div class="req-header">
          <span class="req-name" title=${req.title}>${req.name}</span>
          <div class="req-badges">
            <span class="badge ${confClass}">${Math.round(req.confidence * 100)}%</span>
            ${priorityDef
              ? html`<span class="badge badge-neutral" style="color: ${priorityDef.color};">${priorityDef.label}</span>`
              : nothing}
          </div>
        </div>
        ${req.title ? html`<div style="font-size: var(--r5-text-xs); font-weight: 500;">${req.title}</div>` : nothing}
        <div class="req-text">${req.text}</div>
        <div class="req-footer">
          <div style="display: flex; gap: var(--r5-space-1); align-items: center; flex-wrap: wrap;">
            ${req.depth > 0
              ? html`<span class="badge badge-neutral" style="font-size: var(--r5-font-size-badge);">L${req.depth}</span>`
              : nothing}
            ${req.verificationMethod
              ? html`<span class="badge badge-neutral" style="font-size: var(--r5-font-size-badge);">${req.verificationMethod}</span>`
              : nothing}
            ${req.parentId
              ? html`<span class="badge badge-neutral" style="font-size: var(--r5-font-size-badge);" title="Derives from ${req.parentId}">&#8594; ${req.parentId}</span>`
              : nothing}
            ${req.source
              ? html`<span style="font-size: var(--r5-font-size-badge); color: var(--r5-text-muted);">${req.source}</span>`
              : nothing}
          </div>
          <div class="req-actions">
            <ark-button size="xs" variant="ghost" @click=${() => this.ingestion.toggleRequirement(req.id)} title="${req.accepted ? 'Reject' : 'Accept'}">
              <ark-icon name="${req.accepted ? 'x' : 'check'}" size="xs"></ark-icon>
            </ark-button>
            <ark-button size="xs" variant="ghost" @click=${() => this._startEdit(req)} title="Edit">
              <ark-icon name="pencil" size="xs"></ark-icon>
            </ark-button>
            <ark-button size="xs" variant="ghost" @click=${() => this.ingestion.removeRequirement(req.id)} title="Remove">
              <ark-icon name="trash-2" size="xs"></ark-icon>
            </ark-button>
          </div>
        </div>

        ${req.existingMatch
          ? html`
              <div class="dup-warning" style="background: color-mix(in srgb, var(--r5-primary) 5%, transparent); border-color: color-mix(in srgb, var(--r5-primary) 30%, var(--r5-border));">
                <div style="font-weight: 500; color: var(--r5-primary); display: flex; align-items: center; gap: var(--r5-space-1);">
                  <ark-icon name="link" size="xs"></ark-icon>
                  May overlap with existing: <strong>${req.existingMatch}</strong>
                </div>
              </div>
            `
          : nothing}

        ${hasDups
          ? html`
              <div class="dup-warning">
                <div style="font-weight: 500; color: var(--r5-warning-700);">
                  <ark-icon name="alert-triangle" size="xs"></ark-icon>
                  Potential duplicates found
                </div>
                ${req.duplicates!.map(
                  (d) => html`
                    <div class="dup-match">
                      <span class="dup-match-name">${d.name}</span>
                      <span class="dup-similarity">${Math.round(d.similarity * 100)}% similar</span>
                    </div>
                  `,
                )}
              </div>
            `
          : nothing}
      </div>
    `;
  }

  private _renderEditCard(req: ExtractedRequirement) {
    return html`
      <div class="edit-overlay">
        <div class="edit-field">
          <label>Identifier</label>
          <input
            type="text"
            .value=${this._editName}
            @input=${(e: InputEvent) => (this._editName = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="edit-field">
          <label>Title</label>
          <input
            type="text"
            .value=${this._editTitle}
            @input=${(e: InputEvent) => (this._editTitle = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="edit-field">
          <label>Requirement Text</label>
          <textarea
            .value=${this._editText}
            @input=${(e: InputEvent) => (this._editText = (e.target as HTMLTextAreaElement).value)}
          ></textarea>
        </div>
        <div class="edit-field">
          <label>Priority</label>
          <select
            .value=${this._editPriority}
            @change=${(e: Event) => (this._editPriority = (e.target as HTMLSelectElement).value)}
          >
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div class="edit-actions">
          <ark-button size="xs" variant="ghost" @click=${() => (this._editingId = null)}>
            Cancel
          </ark-button>
          <ark-button size="xs" variant="primary" @click=${() => this._saveEdit(req)}>
            Update
          </ark-button>
        </div>
      </div>
    `;
  }

  // ── COMMITTING phase: code preview + diagnostics ────────────────────────

  private _renderCommitPhase() {
    return html`
      ${this.ingestion.isLoading
        ? html`
            <div class="panel-body" style="padding: 0;">
              <div class="progress">
                <ark-icon name="terminal" size="lg"></ark-icon>
                <div>${this.ingestion.progressMessage || 'Generating SysML...'}</div>
                <div class="progress-bar">
                  <div class="progress-bar-inner"></div>
                </div>
              </div>
            </div>
          `
        : html`
            <div class="stats-row">
              <div class="stat">
                Parse:
                <span class="stat-value" style="color: ${this.ingestion.parseSuccess ? 'var(--r5-success-700)' : 'var(--r5-destructive-700)'};">
                  ${this.ingestion.parseSuccess ? 'Valid' : 'Has errors'}
                </span>
              </div>
              <div class="stat">
                <span class="stat-value">${this.ingestion.acceptedRequirements.length}</span> requirements
              </div>
            </div>

            ${this.ingestion.parseDiagnostics.length > 0
              ? html`
                  <div class="diagnostics">
                    ${this.ingestion.parseDiagnostics.map(
                      (d) => html`
                        <div class="diag-item ${d.severity === 'error' ? 'diag-error' : 'diag-warning'}">
                          <ark-icon name="${d.severity === 'error' ? 'x-circle' : 'alert-triangle'}" size="xs"></ark-icon>
                          ${d.line ? `L${d.line}: ` : ''}${d.message}
                        </div>
                      `,
                    )}
                  </div>
                `
              : nothing}

            <div class="code-preview">${this.ingestion.generatedCode}</div>

            <div class="panel-footer">
              <ark-button size="xs" variant="ghost" @click=${() => { this.ingestion.phase = 'reviewing' as never; this.requestUpdate(); }}>
                Back to Review
              </ark-button>
              <span style="flex: 1;"></span>
              <ark-button
                size="xs"
                variant="primary"
                @click=${this._onCommit}
              >
                <ark-icon name="check" size="xs"></ark-icon>
                Commit to Editor
              </ark-button>
            </div>
          `}
    `;
  }

  // ── DONE phase ──────────────────────────────────────────────────────────

  private _renderDonePhase() {
    return html`
      <div class="panel-body" style="padding: 0;">
        <div class="done-state">
          <svg class="done-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <div style="font-weight: 600;">Requirements Committed</div>
          <div style="color: var(--r5-text-muted);">
            ${this.ingestion.acceptedRequirements.length} requirement(s) added to the editor
          </div>
          <ark-button size="xs" variant="outline" @click=${() => this.ingestion.reset()}>
            Ingest Another Document
          </ark-button>
        </div>
      </div>
    `;
  }

  // ── Settings overlay ────────────────────────────────────────────────────

  private _renderSettings() {
    return html`
      <div class="settings">
        <div class="settings-section">Claude API</div>
        <div class="settings-field">
          <label>Anthropic API Key</label>
          <input
            type="password"
            placeholder="sk-ant-..."
            .value=${this._settingsAnthropicKey}
            @input=${(e: InputEvent) => (this._settingsAnthropicKey = (e.target as HTMLInputElement).value)}
          />
        </div>

        <div class="settings-section">Embeddings (Optional)</div>
        <div class="settings-field">
          <label>Provider</label>
          <select
            .value=${this._settingsEmbeddingProvider}
            @change=${(e: Event) => (this._settingsEmbeddingProvider = (e.target as HTMLSelectElement).value as 'voyage' | 'openai')}
          >
            <option value="voyage">Voyage AI (Recommended)</option>
            <option value="openai">OpenAI</option>
          </select>
        </div>
        <div class="settings-field">
          <label>Embedding API Key</label>
          <input
            type="password"
            placeholder="API key..."
            .value=${this._settingsEmbeddingKey}
            @input=${(e: InputEvent) => (this._settingsEmbeddingKey = (e.target as HTMLInputElement).value)}
          />
        </div>

        <div class="settings-section">Vector Database (Optional)</div>
        <div class="settings-field">
          <label>Provider</label>
          <select
            .value=${this._settingsVectorProvider}
            @change=${(e: Event) => (this._settingsVectorProvider = (e.target as HTMLSelectElement).value as 'pinecone' | 'chroma')}
          >
            <option value="pinecone">Pinecone</option>
            <option value="chroma">Chroma</option>
          </select>
        </div>
        <div class="settings-field">
          <label>Endpoint URL</label>
          <input
            type="url"
            placeholder="https://your-index.svc.pinecone.io"
            .value=${this._settingsVectorEndpoint}
            @input=${(e: InputEvent) => (this._settingsVectorEndpoint = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="settings-field">
          <label>Index / Collection Name</label>
          <input
            type="text"
            placeholder="sysml-requirements"
            .value=${this._settingsVectorIndex}
            @input=${(e: InputEvent) => (this._settingsVectorIndex = (e.target as HTMLInputElement).value)}
          />
        </div>
        <div class="settings-field">
          <label>Vector DB API Key</label>
          <input
            type="password"
            placeholder="API key..."
            .value=${this._settingsVectorApiKey}
            @input=${(e: InputEvent) => (this._settingsVectorApiKey = (e.target as HTMLInputElement).value)}
          />
        </div>

        ${this._connectionTestResult
          ? html`
              <div class="warning-item" style="background: ${this._connectionTestResult.startsWith('OK') ? 'var(--r5-success-50)' : 'var(--r5-destructive-50)'}; color: ${this._connectionTestResult.startsWith('OK') ? 'var(--r5-success-700)' : 'var(--r5-destructive-700)'}; border-color: ${this._connectionTestResult.startsWith('OK') ? 'var(--r5-success-200)' : 'var(--r5-destructive-200)'};">
                ${this._connectionTestResult}
              </div>
            `
          : nothing}

        <div class="settings-actions">
          ${this._settingsVectorEndpoint
            ? html`
                <ark-button size="xs" variant="ghost" @click=${this._onTestConnection}>
                  Test Connection
                </ark-button>
              `
            : nothing}
          <ark-button size="xs" variant="ghost" @click=${() => { this.ingestion.showSettings = false; this.requestUpdate(); }}>
            Cancel
          </ark-button>
          <ark-button
            size="xs"
            variant="primary"
            ?disabled=${!this._settingsAnthropicKey.trim()}
            @click=${this._onSaveSettings}
          >
            Save
          </ark-button>
        </div>
      </div>
    `;
  }

  // ── Event handlers ──────────────────────────────────────────────────────

  private _onUploadClick(): void {
    const input = this.shadowRoot?.getElementById('file-input') as HTMLInputElement | null;
    input?.click();
  }

  private _openAuditTrail(): void {
    const journal = this.ingestion.activeJournal ?? this.ingestion.journals[0];
    if (!journal) return;
    this.dispatchEvent(new CustomEvent('open-audit-trail', {
      bubbles: true,
      composed: true,
      detail: { journal },
    }));
  }

  private _onFileSelected(e: Event): void {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.ingestion.handleFileUpload(file);
    }
    // Reset so the same file can be selected again
    input.value = '';
  }

  private _onDragOver(e: DragEvent): void {
    e.preventDefault();
    this._dragOver = true;
  }

  private _onDragLeave(): void {
    this._dragOver = false;
  }

  private _onDrop(e: DragEvent): void {
    e.preventDefault();
    this._dragOver = false;
    const file = e.dataTransfer?.files[0];
    if (file) {
      this.ingestion.handleFileUpload(file);
    }
  }

  private _onPasteSubmit(): void {
    if (this._pasteText.trim()) {
      this.ingestion.handlePastedText(this._pasteText);
      this._pasteText = '';
      this._showPaste = false;
    }
  }

  private _startEdit(req: ExtractedRequirement): void {
    this._editingId = req.id;
    this._editName = req.name;
    this._editTitle = req.title;
    this._editText = req.text;
    this._editPriority = req.priority;
  }

  private _saveEdit(req: ExtractedRequirement): void {
    this.ingestion.updateRequirement(req.id, {
      name: this._editName.trim() || req.name,
      title: this._editTitle.trim() || req.title,
      text: this._editText.trim() || req.text,
      priority: this._editPriority as ExtractedRequirement['priority'],
    });
    this._editingId = null;
  }

  private _onCommit(): void {
    const result = this.ingestion.commit();
    if (result) {
      this.dispatchEvent(
        new CustomEvent('insert-code', {
          detail: { code: result.code, insertionPoint: result.insertionPoint },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private _loadSettingsValues(): void {
    try {
      const raw = localStorage.getItem('sysml:ingestion-api-keys');
      if (raw) {
        const keys = JSON.parse(raw) as IngestionApiKeys;
        this._settingsAnthropicKey = keys.anthropicKey ?? '';
        this._settingsEmbeddingKey = keys.embeddingKey ?? '';
        this._settingsEmbeddingProvider = keys.embeddingProvider ?? 'voyage';
      }

      const vectorRaw = localStorage.getItem('sysml:vector-config');
      if (vectorRaw) {
        const { config, apiKey } = JSON.parse(vectorRaw) as {
          config: { provider: string; endpoint: string; indexName: string };
          apiKey: string;
        };
        this._settingsVectorProvider = config.provider as 'pinecone' | 'chroma';
        this._settingsVectorEndpoint = config.endpoint ?? '';
        this._settingsVectorIndex = config.indexName ?? '';
        this._settingsVectorApiKey = apiKey ?? '';
      }
    } catch {
      // Ignore
    }
  }

  private _onSaveSettings(): void {
    // Save API keys
    this.ingestion.saveApiKeys({
      anthropicKey: this._settingsAnthropicKey,
      embeddingKey: this._settingsEmbeddingKey || undefined,
      embeddingProvider: this._settingsEmbeddingProvider,
    });

    // Save vector config if provided
    if (this._settingsVectorEndpoint && this._settingsVectorIndex) {
      this.ingestion.saveVectorConfig(
        {
          provider: this._settingsVectorProvider,
          endpoint: this._settingsVectorEndpoint,
          indexName: this._settingsVectorIndex,
        },
        this._settingsVectorApiKey,
      );
    }

    this.ingestion.showSettings = false;
    this.requestUpdate();
  }

  private async _onTestConnection(): Promise<void> {
    this._connectionTestResult = 'Testing...';
    this.requestUpdate();

    // Temporarily configure for testing
    this.ingestion.saveVectorConfig(
      {
        provider: this._settingsVectorProvider,
        endpoint: this._settingsVectorEndpoint,
        indexName: this._settingsVectorIndex,
      },
      this._settingsVectorApiKey,
    );

    const result = await this.ingestion.testVectorConnection();
    this._connectionTestResult = result.ok ? `OK: ${result.message}` : result.message;
    this.requestUpdate();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-ingest-panel': SysmlIngestPanel;
  }
}
